<div class="min-h-screen bg-cloud-mist">

  <!-- ── Page header ──────────────────────────────────────────────────────── -->
  <div class="bg-white border-b border-pale-gray">
    <div class="tfr-page-shell-wide py-5
                flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div>
        <p class="text-[10px] font-bold uppercase tracking-[0.2em] text-action-blue mb-0.5">
          Admin &rsaquo; Equipamiento
        </p>
        <h1 class="text-[22px] font-bold text-midnight-indigo leading-tight">Gestión de amenidades</h1>
        <p class="text-[12px] text-steel-gray mt-0.5">
          Usa las flechas para reordenar. Los cambios se publican en tiempo real.
        </p>
      </div>
      <button
        type="button"
        (click)="onCreateAmenity()"
        [disabled]="saving() || reordering()"
        class="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl
               bg-action-blue text-white text-[13px] font-semibold
               transition-all hover:brightness-110 disabled:opacity-50 shrink-0
               focus:outline-none focus:ring-2 focus:ring-action-blue/40">
        <lucide-icon name="plus" [size]="15" />
        Nueva amenidad
      </button>
    </div>
  </div>

  <!-- ── Feature list ─────────────────────────────────────────────────────── -->
  <div class="tfr-page-shell-wide py-8">

    @if (loading()) {
      <div class="bg-white rounded-2xl border border-pale-gray flex items-center justify-center py-20">
        <svg class="animate-spin h-5 w-5 text-action-blue" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
        </svg>
      </div>
    } @else if (grouped().length === 0) {
      <div class="bg-white rounded-2xl border border-pale-gray
                  flex flex-col items-center justify-center py-16 gap-3">
        <lucide-icon name="package-open" [size]="32" class="text-steel-gray" />
        <p class="text-[13px] text-steel-gray">Sin amenidades. Crea la primera con el botón superior.</p>
      </div>
    } @else {
      <div class="flex flex-col gap-4">
        @for (group of grouped(); track group.category.id) {
          <section class="bg-white rounded-2xl border border-pale-gray overflow-hidden">

            <!-- Group header -->
            <div class="flex items-center gap-2.5 px-5 py-3 bg-cloud-mist border-b border-pale-gray">
              <span class="flex items-center justify-center h-7 w-7 rounded-lg bg-white border border-pale-gray shrink-0">
                <lucide-icon [name]="group.category.icon" [size]="13" class="text-slate-blue" />
              </span>
              <span class="text-[11px] font-bold uppercase tracking-[0.2em] text-midnight-indigo">
                {{ group.category.label }}
              </span>
              <span class="ml-auto text-[11px] text-steel-gray tabular-nums">
                {{ group.features.length }}
              </span>
            </div>

            <!-- Feature rows -->
            @for (f of group.features; track f.id) {
              <div
                class="flex items-center gap-3 px-4 py-3 border-b border-pale-gray last:border-0
                       transition-colors hover:bg-cloud-mist/40"
                [class.opacity-40]="!f.isActive">

                <!-- Reorder arrows -->
                <div class="flex flex-col shrink-0">
                  <button
                    type="button"
                    (click)="moveFeatureUp(f)"
                    [disabled]="isFirstInGroup(f) || reordering()"
                    title="Subir"
                    class="p-0.5 rounded text-steel-gray hover:text-midnight-indigo
                           disabled:opacity-20 disabled:cursor-not-allowed transition-colors
                           focus:outline-none">
                    <lucide-icon name="chevron-up" [size]="14" />
                  </button>
                  <button
                    type="button"
                    (click)="moveFeatureDown(f)"
                    [disabled]="isLastInGroup(f) || reordering()"
                    title="Bajar"
                    class="p-0.5 rounded text-steel-gray hover:text-midnight-indigo
                           disabled:opacity-20 disabled:cursor-not-allowed transition-colors
                           focus:outline-none">
                    <lucide-icon name="chevron-down" [size]="14" />
                  </button>
                </div>

                <!-- Icon + name + group -->
                <div class="flex items-center gap-2.5 flex-1 min-w-0">
                  <span class="flex items-center justify-center h-8 w-8 rounded-lg bg-pale-gray shrink-0">
                    <lucide-icon [name]="f.icon" [size]="14" class="text-slate-blue" />
                  </span>
                  <div class="min-w-0">
                    <p class="text-[13px] font-medium text-midnight-indigo truncate leading-snug">
                      {{ f.label }}
                    </p>
                    <p class="text-[11px] text-steel-gray leading-none mt-0.5">
                      {{ categoryName(f.categoryId) }}
                    </p>
                  </div>
                </div>

                <!-- Allowed pills -->
                <div class="hidden sm:flex flex-wrap gap-1 shrink-0 max-w-[200px]">
                  @for (cat of allowedPills(f); track cat) {
                    <span
                      class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border"
                      [class.bg-midnight-indigo]="cat === 'global'"
                      [class.text-white]="cat === 'global'"
                      [class.border-transparent]="cat === 'global'"
                      [class.bg-pale-gray]="cat !== 'global'"
                      [class.text-slate-blue]="cat !== 'global'"
                      [class.border-pale-gray]="cat !== 'global'">
                      {{ pillLabel(cat) }}
                    </span>
                  }
                </div>

                <!-- Active toggle -->
                <button
                  type="button"
                  (click)="onToggleActiveStatus(f)"
                  [title]="f.isActive ? 'Desactivar' : 'Activar'"
                  class="relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full
                         transition-colors duration-200
                         focus:outline-none focus:ring-2 focus:ring-action-blue/30 focus:ring-offset-1"
                  [class.bg-action-blue]="f.isActive"
                  [class.bg-steel-gray]="!f.isActive">
                  <span
                    class="pointer-events-none absolute top-0.5 left-0.5 h-4 w-4 rounded-full
                           bg-white shadow-sm transition-transform duration-200"
                    [class.translate-x-4]="f.isActive"
                    [class.translate-x-0]="!f.isActive">
                  </span>
                </button>

                <!-- Edit + Delete -->
                <div class="flex items-center gap-0.5 shrink-0">
                  <button
                    type="button"
                    (click)="onEditAmenity(f)"
                    title="Editar"
                    class="p-1.5 rounded-lg text-steel-gray hover:text-midnight-indigo
                           hover:bg-pale-gray transition-colors focus:outline-none">
                    <lucide-icon name="pencil" [size]="13" />
                  </button>
                  <button
                    type="button"
                    (click)="confirmDelete(f.id!)"
                    title="Eliminar"
                    class="p-1.5 rounded-lg text-steel-gray hover:text-red-600
                           hover:bg-red-50 transition-colors focus:outline-none">
                    <lucide-icon name="trash-2" [size]="13" />
                  </button>
                </div>

              </div>
            }
          </section>
        }
      </div>
    }

  </div>

  <!-- ── Drawer: formulario lateral ──────────────────────────────────────── -->
  @if (editingId()) {

    <!-- Backdrop -->
    <div
      class="fixed inset-0 z-40 bg-black/25 backdrop-blur-[2px]"
      (click)="cancelForm()">
    </div>

    <!-- Panel lateral derecho -->
    <aside
      class="fixed inset-y-0 right-0 z-50 flex flex-col
             w-full max-w-md bg-white border-l border-pale-gray shadow-[−8px_0_32px_rgba(11,53,88,0.08)]">

      <!-- Drawer header (fijo) -->
      <div class="flex items-center justify-between px-5 py-4 border-b border-pale-gray shrink-0">
        <div>
          <p class="text-[10px] font-bold uppercase tracking-[0.2em] text-steel-gray mb-0.5">
            {{ editingId() === 'new' ? 'Crear' : 'Editar' }}
          </p>
          <p class="text-[15px] font-bold text-midnight-indigo">
            {{ editingId() === 'new' ? 'Nueva amenidad' : 'Editar amenidad' }}
          </p>
        </div>
        <button
          type="button"
          (click)="cancelForm()"
          class="p-1.5 rounded-lg text-steel-gray hover:text-midnight-indigo
                 hover:bg-pale-gray transition-colors focus:outline-none">
          <lucide-icon name="x" [size]="18" />
        </button>
      </div>

      <!-- Formulario (scrollable) -->
      <form
        [formGroup]="form"
        (ngSubmit)="saveForm()"
        novalidate
        class="flex-1 overflow-y-auto px-5 py-5 flex flex-col gap-5">

        <!-- Nombre -->
        <div>
          <label class="block text-[10px] font-bold uppercase tracking-[0.2em] text-steel-gray mb-1.5">
            Nombre <span class="text-red-400 normal-case tracking-normal">*</span>
          </label>
          <input
            formControlName="label"
            type="text"
            placeholder="Ej. Proyector 4K"
            class="w-full rounded-xl border px-3.5 py-2.5 text-[13px] text-midnight-indigo
                   placeholder:text-steel-gray bg-white transition-colors
                   focus:outline-none focus:ring-2 focus:ring-action-blue/20"
            [class.border-pale-gray]="!hasError('label')"
            [class.border-red-300]="hasError('label')" />
          @if (hasError('label')) {
            <p class="text-[11px] text-red-500 mt-1">Mínimo 2 caracteres.</p>
          }
        </div>

        <!-- Icono -->
        <div>
          <label class="block text-[10px] font-bold uppercase tracking-[0.2em] text-steel-gray mb-1.5">
            Icono Lucide <span class="text-red-400 normal-case tracking-normal">*</span>
          </label>
          <div class="flex gap-2 items-center">
            <input
              formControlName="icon"
              type="text"
              placeholder="wifi, tv, projector…"
              class="flex-1 rounded-xl border px-3.5 py-2.5 text-[13px] text-midnight-indigo
                     placeholder:text-steel-gray bg-white transition-colors
                     focus:outline-none focus:ring-2 focus:ring-action-blue/20"
              [class.border-pale-gray]="!hasError('icon')"
              [class.border-red-300]="hasError('icon')" />
            <span class="flex items-center justify-center h-10 w-10 rounded-xl
                         bg-pale-gray border border-pale-gray shrink-0">
              <lucide-icon [name]="iconPreview()" [size]="18" class="text-slate-blue" />
            </span>
          </div>
          <p class="text-[10px] text-steel-gray mt-1">
            Nombres en
            <a href="https://lucide.dev/icons" target="_blank" rel="noopener"
               class="text-midnight-indigo underline hover:text-action-blue">lucide.dev</a>
          </p>
        </div>

        <!-- Grupo -->
        <div>
          <label class="block text-[10px] font-bold uppercase tracking-[0.2em] text-steel-gray mb-1.5">
            Grupo <span class="text-red-400 normal-case tracking-normal">*</span>
          </label>
          <div class="relative">
            <select
              formControlName="categoryId"
              class="w-full appearance-none rounded-xl border px-3.5 py-2.5 pr-8
                     text-[13px] text-midnight-indigo bg-white transition-colors
                     focus:outline-none focus:ring-2 focus:ring-action-blue/20"
              [class.border-pale-gray]="!hasError('categoryId')"
              [class.border-red-300]="hasError('categoryId')">
              <option value="" disabled>Selecciona un grupo…</option>
              @for (cat of categories(); track cat.id) {
                <option [value]="cat.id">{{ cat.label }}</option>
              }
            </select>
            <span class="pointer-events-none absolute inset-y-0 right-3 flex items-center text-steel-gray">
              <lucide-icon name="chevron-down" [size]="14" />
            </span>
          </div>
        </div>

        <!-- Visible en tipos de espacio -->
        <div>
          <label class="block text-[10px] font-bold uppercase tracking-[0.2em] text-steel-gray mb-2">
            Visible en tipos de espacio
          </label>
          <div class="flex flex-col gap-1.5">

            <button
              type="button"
              (click)="toggleAllowedCategory('global')"
              class="w-full rounded-xl border px-3.5 py-2 text-left
                     text-[12px] font-medium transition-all duration-150
                     focus:outline-none focus:ring-2 focus:ring-action-blue/20"
              [class.bg-midnight-indigo]="isAllowedSelected('global')"
              [class.border-midnight-indigo]="isAllowedSelected('global')"
              [class.text-white]="isAllowedSelected('global')"
              [class.bg-white]="!isAllowedSelected('global')"
              [class.border-pale-gray]="!isAllowedSelected('global')"
              [class.text-slate-blue]="!isAllowedSelected('global')">
              Todos los espacios (global)
            </button>

            <div class="grid grid-cols-2 gap-1.5">
              @for (cat of SPACE_CATEGORIES; track cat) {
                <button
                  type="button"
                  (click)="toggleAllowedCategory(cat)"
                  class="rounded-xl border px-3 py-2 text-center
                         text-[11px] font-medium transition-all duration-150
                         focus:outline-none focus:ring-2 focus:ring-action-blue/20"
                  [class.bg-midnight-indigo]="isAllowedSelected(cat)"
                  [class.border-midnight-indigo]="isAllowedSelected(cat)"
                  [class.text-white]="isAllowedSelected(cat)"
                  [class.bg-white]="!isAllowedSelected(cat)"
                  [class.border-pale-gray]="!isAllowedSelected(cat)"
                  [class.text-slate-blue]="!isAllowedSelected(cat)">
                  {{ SPACE_CATEGORY_LABELS[cat] }}
                </button>
              }
            </div>
          </div>
        </div>

        <!-- Activo -->
        <div class="flex items-center justify-between py-1">
          <label class="text-[10px] font-bold uppercase tracking-[0.2em] text-steel-gray">
            Activo
          </label>
          <button
            type="button"
            (click)="form.controls.isActive.setValue(!form.controls.isActive.value)"
            class="relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full
                   transition-colors duration-200
                   focus:outline-none focus:ring-2 focus:ring-action-blue/30 focus:ring-offset-1"
            [class.bg-action-blue]="form.controls.isActive.value"
            [class.bg-steel-gray]="!form.controls.isActive.value">
            <span
              class="pointer-events-none absolute top-0.5 left-0.5 h-4 w-4 rounded-full
                     bg-white shadow-sm transition-transform duration-200"
              [class.translate-x-4]="form.controls.isActive.value"
              [class.translate-x-0]="!form.controls.isActive.value">
            </span>
          </button>
        </div>

        @if (saveError()) {
          <p class="text-[11px] text-red-600 bg-red-50 border border-red-100
                     rounded-xl px-3.5 py-2.5">
            {{ saveError() }}
          </p>
        }

      </form>

      <!-- Botones (fijos en la parte inferior) -->
      <div class="px-5 py-4 border-t border-pale-gray flex items-center gap-3 shrink-0 bg-white">
        <button
          type="button"
          (click)="cancelForm()"
          class="text-[13px] font-medium text-steel-gray hover:text-midnight-indigo
                 transition-colors focus:outline-none px-2">
          Cancelar
        </button>
        <button
          type="button"
          (click)="saveForm()"
          [disabled]="saving()"
          class="flex-1 inline-flex items-center justify-center gap-2
                 rounded-xl bg-action-blue text-white py-2.5 text-[13px] font-semibold
                 transition-all hover:brightness-110 disabled:opacity-50
                 focus:outline-none focus:ring-2 focus:ring-action-blue/40">
          @if (saving()) {
            <svg class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
            </svg>
            Guardando&hellip;
          } @else {
            Guardar cambios
          }
        </button>
      </div>

    </aside>
  }

  <!-- ── Delete confirmation modal ──────────────────────────────────────── -->
  @if (deleteId()) {
    <div
      class="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4
             bg-black/30 backdrop-blur-[2px]"
      (click)="cancelDelete()">
      <div
        class="bg-white rounded-2xl border border-pale-gray w-full max-w-sm p-6"
        (click)="$event.stopPropagation()">
        <p class="text-[10px] font-bold uppercase tracking-[0.2em] text-steel-gray mb-3">
          Confirmar eliminación
        </p>
        <p class="text-[14px] font-semibold text-midnight-indigo mb-1">¿Eliminar esta amenidad?</p>
        <p class="text-[12px] text-steel-gray mb-6">
          Esta acción es permanente. Los IDs en los espacios existentes se conservan pero no se mostrarán.
        </p>
        <div class="flex gap-3">
          <button
            type="button"
            (click)="cancelDelete()"
            class="flex-1 rounded-xl border border-pale-gray py-2.5 text-[13px] font-medium
                   text-slate-blue hover:bg-cloud-mist transition-colors focus:outline-none">
            Cancelar
          </button>
          <button
            type="button"
            (click)="executeDelete()"
            [disabled]="saving()"
            class="flex-1 rounded-xl bg-red-600 hover:bg-red-700 py-2.5 text-[13px]
                   font-semibold text-white transition-colors disabled:opacity-50 focus:outline-none">
            {{ saving() ? 'Eliminando…' : 'Eliminar' }}
          </button>
        </div>
      </div>
    </div>
  }

</div>
